import type { BoardPoint, BoardSize } from '@wx-weiqi/contracts';
import { boardCoordinate, type BoardViewport } from './board-geometry';
import { nextBoardFocus } from './game-interaction';

export interface BoardFocusBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** Visible intersections only. This constrains navigation, never move legality. */
export function boardViewportFocusBounds(
  size: BoardSize,
  viewport: BoardViewport | null,
): BoardFocusBounds | null {
  if (!viewport) return { minX: 0, maxX: size - 1, minY: 0, maxY: size - 1 };
  const indices = Array.from({ length: size }, (_, index) => index);
  const columns = indices.filter((index) => {
    const coordinate = boardCoordinate(size, index);
    return coordinate >= viewport.x && coordinate <= viewport.x + viewport.width;
  });
  const rows = indices.filter((index) => {
    const coordinate = boardCoordinate(size, index);
    return coordinate >= viewport.y && coordinate <= viewport.y + viewport.height;
  });
  if (!columns.length || !rows.length) return null;
  return {
    minX: columns[0]!,
    maxX: columns.at(-1)!,
    minY: rows[0]!,
    maxY: rows.at(-1)!,
  };
}

export function boardPointWithinFocus(point: BoardPoint, bounds: BoardFocusBounds | null) {
  return Boolean(
    bounds &&
    point.x >= bounds.minX &&
    point.x <= bounds.maxX &&
    point.y >= bounds.minY &&
    point.y <= bounds.maxY,
  );
}

export function visibleBoardFocus(index: number, size: BoardSize, bounds: BoardFocusBounds | null) {
  if (!bounds) return null;
  const safeIndex = Math.max(0, Math.min(size * size - 1, index));
  const x = Math.max(bounds.minX, Math.min(bounds.maxX, safeIndex % size));
  const y = Math.max(bounds.minY, Math.min(bounds.maxY, Math.floor(safeIndex / size)));
  return y * size + x;
}

export function nextVisibleBoardFocus(
  index: number,
  size: BoardSize,
  key: string,
  bounds: BoardFocusBounds | null,
) {
  const current = visibleBoardFocus(index, size, bounds);
  return current === null
    ? null
    : visibleBoardFocus(nextBoardFocus(current, size, key), size, bounds);
}
